import { useState, useEffect } from 'react';
import api from '../api';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { RefreshCw, DollarSign, TrendingUp, Award, Download } from 'lucide-react';
import toast from 'react-hot-toast';

const usd = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function StatCard({ label, value, icon: Icon, small }) {
  return (
    <div className="bg-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <div className={`font-bold text-white ${small ? 'text-lg' : 'text-2xl'}`}>{value}</div>
          <div className="text-sm text-gray-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function FreePlayByMonth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/reports/free-play-by-month', { params: { months } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load Free Play report');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [months]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.months || [];
  const summary = data?.summary;
  const peakKey = summary?.peak?.key || null;

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['Month', 'Free Play Redeemed ($)'];
    const csv = [header, ...rows.map((r) => [r.label, r.value])]
      .map((row) => row.map((c) => `"${c}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'free-play-by-month.csv';
    link.click();
  };

  const manyBars = rows.length > 12;

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-gray-800 rounded-xl p-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <DollarSign className="text-amber-500" size={20} />
            Free Play Redeemed by Month
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Dollar value of Free Play rewards redeemed each calendar month (casino time).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500"
          >
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
          </select>
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 text-amber-500 animate-spin mb-4" />
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : !rows.length ? (
        <div className="bg-gray-800 rounded-xl py-16 text-center text-gray-400">
          No Free Play redemptions found.
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label={`Trailing ${summary?.monthsBack || months} months`} value={usd(summary?.total)} icon={DollarSign} />
            <StatCard label="Avg / month" value={usd(summary?.average)} icon={TrendingUp} />
            <StatCard
              label="Peak month"
              value={summary?.peak ? `${summary.peak.label} · ${usd(summary.peak.value)}` : '—'}
              icon={Award}
              small
            />
          </div>

          {/* Chart */}
          <div className="bg-gray-800 rounded-xl p-6">
            <div style={{ width: '100%', height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(142,142,147,0.15)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    axisLine={{ stroke: '#374151' }}
                    tickLine={false}
                    interval={0}
                    angle={manyBars ? -45 : 0}
                    textAnchor={manyBars ? 'end' : 'middle'}
                    height={manyBars ? 64 : 30}
                  />
                  <YAxis
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `$${Math.round(v / 1000)}k`}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(212,168,67,0.1)' }}
                    contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8, color: '#fff' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(v) => [usd(v), 'Redeemed']}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    {rows.map((r) => (
                      <Cell key={r.key} fill={r.key === peakKey ? '#F59E0B' : '#D4A843'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
